import React, { useState, useCallback, useEffect } from 'react';
import { useMandantStore } from '../../../store/mandantStore';
import { LEISTUNGEN, LEISTUNG_MAP } from '../data/leistungskatalog';
import { buildRechnung, validateRechnung, formatEuroDisplay, formatDateDE } from '../services/rechnungBuilder';
import { generateRechnungPDF, rechnungDateiname } from '../services/pdfGenerator';
import { downloadEbInterface } from '../services/ebInterface';
import { AUSSTELLER_DEFAULT, type Rechnungsadresse, type Rechnung, type Aussteller } from '../types';
import { toMandantID } from '../../../types/branded';
import { saveRechnung, nextLaufNr, loadAusstellerForRechnung } from '../../../services/rechnungenService';
import { logAktion } from '../../../services/auditLogService';

// ── Typen ──────────────────────────────────────────────────────────────────

interface PosInput { code: string; menge: number; }

interface FormState {
  empfaenger:    Rechnungsadresse;
  positionen:    PosInput[];
  zahlungsfrist: number;
  leistungszeitraum: string;
  anmerkungen:   string;
}

const EMPTY_EMP: Rechnungsadresse = {
  firma: '', strasse: '', plz: '', ort: '', land: 'AT',
};

const INIT_FORM: FormState = {
  empfaenger:    EMPTY_EMP,
  positionen:    [{ code: 'VGÜ-02', menge: 1 }],
  zahlungsfrist: 30,
  leistungszeitraum: '',
  anmerkungen:   '',
};

// ── Positions-Zeile ────────────────────────────────────────────────────────

function PosZeile({
  pos, onChange, onRemove,
}: {
  pos: PosInput;
  onChange: (p: PosInput) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const l = LEISTUNG_MAP.get(pos.code);

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <select
        value={pos.code}
        onChange={e => { onChange({ ...pos, code: e.target.value }); }}
        className="input-field flex-1 text-sm"
      >
        {LEISTUNGEN.map(l => (
          <option key={l.code} value={l.code}>{l.code} — {l.bezeichnung}</option>
        ))}
      </select>
      <input
        type="number"
        min={0.25}
        step={0.25}
        value={pos.menge}
        onChange={e => { onChange({ ...pos, menge: parseFloat(e.target.value) || 1 }); }}
        className="input-field w-20 text-center text-sm"
      />
      <span className="text-xs text-gray-500 w-12 text-right flex-shrink-0">
        {l !== undefined ? `${l.einheit}` : ''}
      </span>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-24 text-right flex-shrink-0">
        {l !== undefined ? formatEuroDisplay(l.preisNetto) : '—'}
      </span>
      <button
        onClick={onRemove}
        className="p-1 text-red-400 hover:text-red-600 flex-shrink-0"
        title="Entfernen"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Vorschau ──────────────────────────────────────────────────────────────

function RechnungVorschau({ rechnung, onDownload, onDownloadXml }: {
  rechnung:     Rechnung;
  onDownload:   () => void;
  onDownloadXml: () => void;
}): React.JSX.Element {
  return (
    <div className="card space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-bold text-blue-700 dark:text-blue-400 text-lg">
            RECHNUNG {rechnung.rechnungsNr}
          </h3>
          <p className="text-sm text-gray-500">
            Datum: {formatDateDE(rechnung.datum)} · Fällig: {formatDateDE(rechnung.faelligAm)}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onDownload} className="btn-primary text-sm">↓ PDF</button>
          <button onClick={onDownloadXml} className="btn-secondary text-sm">↓ eRechnung</button>
        </div>
      </div>

      {/* Empfänger */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm">
        <p className="font-medium text-gray-900 dark:text-gray-100">{rechnung.empfaenger.firma}</p>
        <p className="text-gray-500">{rechnung.empfaenger.strasse}</p>
        <p className="text-gray-500">{rechnung.empfaenger.plz} {rechnung.empfaenger.ort}</p>
      </div>

      {/* Positionen */}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left py-1.5 text-xs text-gray-500">Leistung</th>
            <th className="text-right py-1.5 text-xs text-gray-500">Menge</th>
            <th className="text-right py-1.5 text-xs text-gray-500">Netto</th>
            <th className="text-right py-1.5 text-xs text-gray-500">USt</th>
          </tr>
        </thead>
        <tbody>
          {rechnung.positionen.map(p => (
            <tr key={p.leistungCode} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-1.5 text-gray-900 dark:text-gray-100">{p.bezeichnung}</td>
              <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">
                {p.menge} {p.einheit}
              </td>
              <td className="py-1.5 text-right font-mono">{formatEuroDisplay(p.gesamtNetto)}</td>
              <td className="py-1.5 text-right text-gray-500 text-xs">
                {p.ustSatz === 0 ? 'stfr.' : `${p.ustSatz}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summen */}
      <div className="space-y-1 text-sm border-t border-gray-200 dark:border-gray-700 pt-3">
        <div className="flex justify-between text-gray-500">
          <span>Netto gesamt</span>
          <span className="font-mono">{formatEuroDisplay(rechnung.summeNetto)}</span>
        </div>
        <div className="flex justify-between text-gray-500">
          <span>USt</span>
          <span className="font-mono">{formatEuroDisplay(rechnung.summeUst)}</span>
        </div>
        <div className="flex justify-between font-bold text-base text-blue-700 dark:text-blue-400 pt-1 border-t border-gray-200 dark:border-gray-700">
          <span>GESAMT</span>
          <span className="font-mono">{formatEuroDisplay(rechnung.summeBrutto)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function RechnungEditor(): React.JSX.Element {
  const mandant   = useMandantStore(s => s.getAktiverMandant());
  const [form, setForm]         = useState<FormState>(INIT_FORM);
  const [preview, setPreview]   = useState<Rechnung | null>(null);
  const [errors, setErrors]     = useState<string[]>([]);
  const [laufNr, setLaufNr]     = useState(1);
  const [aussteller, setAussteller] = useState<Aussteller>(AUSSTELLER_DEFAULT);
  const [saved, setSaved]       = useState(false);

  useEffect(() => {
    void loadAusstellerForRechnung().then(setAussteller);
  }, []);

  useEffect(() => {
    if (mandant !== null && mandant !== undefined) {
      void nextLaufNr(mandant.kurz_code).then(setLaufNr);
    }
  }, [mandant?.id]);

  const setEmp = useCallback((patch: Partial<Rechnungsadresse>): void => {
    setForm(f => ({ ...f, empfaenger: { ...f.empfaenger, ...patch } }));
  }, []);

  const addPos = (): void => {
    setForm(f => ({ ...f, positionen: [...f.positionen, { code: 'BER-01', menge: 1 }] }));
  };

  const updatePos = (i: number, p: PosInput): void => {
    setForm(f => {
      const next = [...f.positionen];
      next[i] = p;
      return { ...f, positionen: next };
    });
  };

  const removePos = (i: number): void => {
    setForm(f => ({ ...f, positionen: f.positionen.filter((_, j) => j !== i) }));
  };

  const handleVorschau = (): void => {
    if (mandant === null || mandant === undefined) { return; }
    const r = buildRechnung({
      mandantId:    toMandantID(mandant.id),
      mandantKurz:  mandant.kurz_code,
      laufNr,
      empfaenger:   form.empfaenger,
      positionen:   form.positionen.map(p => ({ leistungCode: p.code, menge: p.menge })),
      leistungszeitraum: form.leistungszeitraum !== '' ? form.leistungszeitraum : undefined,
      anmerkungen:  form.anmerkungen !== '' ? form.anmerkungen : undefined,
      zahlungsfrist: form.zahlungsfrist,
    });
    const errs = validateRechnung(r, aussteller.uid);
    setErrors(errs.map(e => e.message));
    setPreview(r);
    setSaved(false);
  };

  const handleSpeichern = (): void => {
    if (preview === null) { return; }
    void saveRechnung({ ...preview, status: 'gestellt' }).then(() => {
      void logAktion(preview.empfaenger.firma, 'RECHNUNG_ERSTELLT', {
        nr:     preview.rechnungsNr,
        brutto: preview.summeBrutto,
      });
      setSaved(true);
      if (mandant !== null && mandant !== undefined) {
        void nextLaufNr(mandant.kurz_code).then(setLaufNr);
      }
    });
  };

  const handleDownload = (): void => {
    if (preview === null) { return; }
    const blob = generateRechnungPDF(preview, aussteller);
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = rechnungDateiname(preview);
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadXml = (): void => {
    if (preview === null) { return; }
    downloadEbInterface(preview, aussteller);
  };

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Neue Rechnung</h1>
        <p className="text-sm text-gray-500 mt-0.5">§11 UStG konforme Rechnungserstellung</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Linke Spalte: Formular */}
        <div className="space-y-5">
          {/* Empfänger */}
          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Empfänger</h2>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="label-text">Firma / Name *</label>
                <input value={form.empfaenger.firma}
                  onChange={e => { setEmp({ firma: e.target.value }); }}
                  className="input-field" placeholder="Ramsauer & Stürmer GmbH" />
              </div>
              <div className="space-y-1">
                <label className="label-text">Straße + Nr.</label>
                <input value={form.empfaenger.strasse}
                  onChange={e => { setEmp({ strasse: e.target.value }); }}
                  className="input-field" placeholder="Musterstraße 1" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="label-text">PLZ</label>
                  <input value={form.empfaenger.plz}
                    onChange={e => { setEmp({ plz: e.target.value }); }}
                    className="input-field" placeholder="8990" />
                </div>
                <div className="col-span-2 space-y-1">
                  <label className="label-text">Ort</label>
                  <input value={form.empfaenger.ort}
                    onChange={e => { setEmp({ ort: e.target.value }); }}
                    className="input-field" placeholder="Bad Aussee" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="label-text">UID-Nummer</label>
                <input value={form.empfaenger.uid ?? ''}
                  onChange={e => { setEmp({ uid: e.target.value !== '' ? e.target.value : undefined }); }}
                  className="input-field" placeholder="ATU12345678" />
              </div>
            </div>
          </div>

          {/* Leistungen */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Leistungen</h2>
              <button onClick={addPos} className="text-xs btn-primary py-1">+ Position</button>
            </div>
            <div className="space-y-2">
              {form.positionen.map((p, i) => (
                <PosZeile
                  key={i}
                  pos={p}
                  onChange={u => { updatePos(i, u); }}
                  onRemove={() => { removePos(i); }}
                />
              ))}
              {form.positionen.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Keine Positionen</p>
              )}
            </div>
          </div>

          {/* Optionen */}
          <div className="card space-y-3">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Optionen</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="label-text">Zahlungsfrist (Tage)</label>
                <input type="number" min={7} max={90}
                  value={form.zahlungsfrist}
                  onChange={e => { setForm(f => ({ ...f, zahlungsfrist: parseInt(e.target.value, 10) || 30 })); }}
                  className="input-field" />
              </div>
              <div className="space-y-1">
                <label className="label-text">Leistungszeitraum</label>
                <input value={form.leistungszeitraum}
                  onChange={e => { setForm(f => ({ ...f, leistungszeitraum: e.target.value })); }}
                  className="input-field" placeholder="z.B. Mai 2025" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="label-text">Anmerkungen</label>
              <textarea value={form.anmerkungen}
                onChange={e => { setForm(f => ({ ...f, anmerkungen: e.target.value })); }}
                rows={2} className="input-field resize-none"
                placeholder="Optionale Hinweise auf der Rechnung" />
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleVorschau} className="btn-primary flex-1 justify-center">
              Vorschau erstellen
            </button>
            {preview !== null && (
              <button onClick={handleSpeichern}
                className={`text-sm px-4 rounded-lg font-medium border transition-colors
                  ${saved
                    ? 'bg-green-100 text-green-700 border-green-200'
                    : 'bg-blue-600 text-white hover:bg-blue-700 border-transparent'
                  }`}>
                {saved ? '✓ Gespeichert' : '💾 Speichern'}
              </button>
            )}
          </div>
        </div>

        {/* Rechte Spalte: Vorschau */}
        <div className="space-y-4">
          {errors.length > 0 && (
            <div className="card border-red-200 dark:border-red-800 space-y-2">
              <h3 className="font-medium text-red-700 dark:text-red-400 text-sm">
                §11 UStG — Pflichtangaben fehlen
              </h3>
              {errors.map(e => (
                <p key={e} className="text-xs text-red-600 dark:text-red-400">• {e}</p>
              ))}
            </div>
          )}

          {preview !== null ? (
            <RechnungVorschau rechnung={preview} onDownload={handleDownload} onDownloadXml={handleDownloadXml} />
          ) : (
            <div className="card text-center py-16 text-gray-400">
              <p className="text-4xl mb-3">📄</p>
              <p className="font-medium">Vorschau erscheint hier</p>
              <p className="text-sm mt-1">Formular ausfüllen und „Vorschau erstellen" klicken</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
